import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, History } from "lucide-react";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/Button";

interface AuditItem {
  id: number;
  accion: string;
  entidad: string;
  entidad_id: number | null;
  created_at: string;
  usuario: string | null;
}

interface AuditResponse {
  data: AuditItem[];
}

const ACTION_LABELS: Record<string, string> = {
  crear_prospect_crm: "Creó el prospect",
  crear_cliente_crm: "Creó la empresa",
  editar_cliente_crm: "Actualizó los datos de la empresa",
  editar_empresa_comercial_crm: "Actualizó los datos comerciales",
  crear_contacto_crm: "Añadió un contacto",
  editar_contacto_crm: "Actualizó un contacto",
  crear_oportunidad_crm: "Creó una oportunidad",
  editar_oportunidad_crm: "Actualizó una oportunidad",
  crear_actividad_crm: "Registró una actividad",
  editar_actividad_crm: "Actualizó una actividad",
  crear_tarea_crm: "Creó una próxima acción",
  cambiar_estado_tarea_crm: "Cambió el estado de una tarea",
  crear_poliza_crm: "Creó una póliza",
  editar_poliza_crm: "Actualizó una póliza",
  crear_vigencia_poliza_crm: "Añadió una vigencia de póliza",
  crear_evento_google_calendar: "Añadió una tarea a Google Calendar",
  actualizar_evento_google_calendar: "Actualizó una tarea en Google Calendar",
};

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error ?? "No se pudo cargar el historial";
  }
  return "No se pudo cargar el historial";
}

function formatDateTime(value: string): string {
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(normalized));
}

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, " ");
}

export function CrmEmpresaAuditoria({ empresaId }: { empresaId: number }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AuditItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refresh = () => setRefreshKey((current) => current + 1);
    window.addEventListener("crm:seguimiento-actualizado", refresh);
    return () => {
      window.removeEventListener("crm:seguimiento-actualizado", refresh);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get<AuditResponse>(
        `/admin/crm/seguimiento/empresa/${empresaId}/auditoria`
      )
      .then((response) => {
        if (!cancelled) setItems(response.data.data);
      })
      .catch((loadError: unknown) => {
        if (!cancelled) setError(getErrorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [empresaId, open, refreshKey]);

  return (
    <section className="space-y-4 border-t border-gray-200 pt-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">Historial de cambios</h3>
          <p className="mt-1 text-sm text-gray-600">
            Registro de quién realizó cada cambio y cuándo.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setOpen((current) => !current)}
        >
          <History className="h-4 w-4" />
          {open ? "Ocultar historial" : "Ver historial"}
          {open ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      {open && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          {loading ? (
            <div className="py-5 text-center text-sm text-gray-500">
              Cargando historial…
            </div>
          ) : error ? (
            <div className="text-sm text-red-700">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-5 text-center text-sm text-gray-500">
              Todavía no hay cambios registrados para esta empresa.
            </div>
          ) : (
            <ol className="space-y-3">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-3"
                >
                  <div className="text-sm font-medium text-gray-900">
                    {actionLabel(item.accion)}
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {formatDateTime(item.created_at)} · {item.usuario ?? "Sistema"}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
